import React, { useState } from 'react';
import { CertStatus, IndFormData } from './types';
import { ImageUploadBox, Select } from '@/components/common';

interface IndividualCertFormProps {
  individualStatus: CertStatus;
  rejectReason?: string;
  formData: IndFormData;
  submitting: boolean;
  onChange: (data: Partial<IndFormData>) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

// 校验正则表达式
const PHONE_REGEX = /^1[3-9]\d{9}$/;
const ID_CARD_REGEX = /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/;

// 个人证件类型选项列表
const INDIVIDUAL_ID_TYPE_OPTIONS = [
  { value: 'ID_CARD', label: '居民身份证' },
  { value: 'HK_RESIDENCE_PERMIT', label: '港澳居民居住证' },
  { value: 'MACAU_RESIDENCE_PERMIT', label: '澳门居民居住证' },
  { value: 'TAIWAN_RESIDENCE_PERMIT', label: '台湾居民居住证' },
  { value: 'HK_MACAU_TAIWAN_RESIDENCE_PERMIT', label: '港澳台居民居住证' },
  { value: 'FOREIGN_PERMANENT_RESIDENCE', label: '外国人永久居留身份证' },
  { value: 'HK_MACAU_HOME_RETURN_PERMIT', label: '港澳居民来往内地通行证(回乡证)' },
  { value: 'TAIWAN_COMPATRIOT_PERMIT', label: '台湾居民来往大陆通行证(台胞证)' },
];

export const IndividualCertForm: React.FC<IndividualCertFormProps> = ({
  individualStatus,
  rejectReason,
  formData,
  submitting,
  onChange,
  onSubmit,
  onCancel,
}) => {
  const [errors, setErrors] = useState<{ idNumber?: string; contactPhone?: string }>({});

  const currentIdType = formData.idType || 'ID_CARD';

  const validateField = (field: 'idNumber' | 'contactPhone', value: string) => {
    let errorMsg = '';
    if (field === 'idNumber') {
      if (value) {
        if (currentIdType === 'ID_CARD' && !ID_CARD_REGEX.test(value)) {
          errorMsg = '请输入正确的 18 位身份证号码';
        } else if (value.trim().length === 0) {
          errorMsg = '请输入证件号码';
        }
      }
    } else if (field === 'contactPhone') {
      if (value && !PHONE_REGEX.test(value)) {
        errorMsg = '请输入正确的 11 位手机号码';
      }
    }
    setErrors((prev) => ({ ...prev, [field]: errorMsg }));
  };

  const validateAll = () => {
    const newErrors: { idNumber?: string; contactPhone?: string } = {};

    if (!formData.idNumber) {
      newErrors.idNumber = '请输入证件号码';
    } else if (currentIdType === 'ID_CARD' && !ID_CARD_REGEX.test(formData.idNumber)) {
      newErrors.idNumber = '请输入正确的 18 位身份证号码';
    }

    if (!formData.contactPhone || !PHONE_REGEX.test(formData.contactPhone)) {
      newErrors.contactPhone = '请输入正确的 11 位手机号码';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) {
      return;
    }
    onSubmit(e);
  };

  return (
    <div className="border-t border-background-200/70 pt-6">
      {individualStatus === 'rejected' && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100/60">
          <p className="text-xs text-red-600">
            <strong>驳回原因：</strong>{rejectReason || '您的实名认证未通过审核，请仔细核对后重新提交。'}
          </p>
        </div>
      )}
      <h3 className="font-semibold text-sm text-foreground-950 mb-4">填写个人认证信息</h3>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>真实姓名
            </label>
            <input
              type="text"
              value={formData.realName}
              onChange={(e) => onChange({ realName: e.target.value })}
              placeholder="请输入真实姓名"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 focus:ring-2 focus:ring-primary-100/50 transition-all"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>证件类型
            </label>
            <Select
              options={INDIVIDUAL_ID_TYPE_OPTIONS}
              value={currentIdType}
              onChange={(val) => onChange({ idType: val as Api.PersonalCertification.IdType })}
              placeholder="请选择证件类型"
              size="md"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>证件号码
            </label>
            <input
              type="text"
              maxLength={20}
              value={formData.idNumber}
              onChange={(e) => {
                const val = e.target.value;
                onChange({ idNumber: val });
                if (errors.idNumber) validateField('idNumber', val);
              }}
              onBlur={(e) => validateField('idNumber', e.target.value)}
              placeholder={currentIdType === 'ID_CARD' ? '请输入 18 位身份证号码' : '请输入证件号码'}
              className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${
                errors.idNumber
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
              }`}
              required
            />
            {errors.idNumber && (
              <p className="text-xs text-red-500 mt-1">{errors.idNumber}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>联系人姓名
            </label>
            <input
              type="text"
              value={formData.contactName}
              onChange={(e) => onChange({ contactName: e.target.value })}
              placeholder="请输入联系人姓名"
              className="w-full px-4 py-3 rounded-lg border border-gray-200 bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:border-primary-400/60 focus:ring-2 focus:ring-primary-100/50 transition-all"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">
              <span className="text-red-500 mr-1">*</span>联系人手机号
            </label>
            <input
              type="tel"
              maxLength={11}
              value={formData.contactPhone}
              onChange={(e) => {
                const val = e.target.value;
                onChange({ contactPhone: val });
                if (errors.contactPhone) validateField('contactPhone', val);
              }}
              onBlur={(e) => validateField('contactPhone', e.target.value)}
              placeholder="请输入 11 位手机号码"
              className={`w-full px-4 py-3 rounded-lg border bg-white text-sm text-foreground-950 placeholder-foreground-400 outline-none focus:ring-2 transition-all ${
                errors.contactPhone
                  ? 'border-red-400 focus:border-red-500 focus:ring-red-100'
                  : 'border-gray-200 focus:border-primary-400/60 focus:ring-primary-100/50'
              }`}
              required
            />
            {errors.contactPhone && (
              <p className="text-xs text-red-500 mt-1">{errors.contactPhone}</p>
            )}
          </div>
        </div>

        {/* 身份证正面照片 */}
        <ImageUploadBox
          label="身份证正面照片"
          required
          value={formData.idFrontAssetId}
          onChange={(assetId) => onChange({ idFrontAssetId: assetId })}
          placeholder="点击或拖拽上传身份证正面"
          subPlaceholder="支持 JPG、PNG 格式，大小不超过 5MB"
        />

        {/* 身份证反面照片 */}
        <ImageUploadBox
          label="身份证反面照片"
          required
          value={formData.idBackAssetId}
          onChange={(assetId) => onChange({ idBackAssetId: assetId })}
          placeholder="点击或拖拽上传身份证反面"
          subPlaceholder="支持 JPG、PNG 格式，大小不超过 5MB"
        />

        {/* 手持身份证照片 */}
        <ImageUploadBox
          label="手持身份证照片"
          required
          value={formData.handheldIdAssetId}
          onChange={(assetId) => onChange({ handheldIdAssetId: assetId })}
          placeholder="点击或拖拽上传手持身份证照片"
          subPlaceholder="需清晰显示面部及身份证信息，支持 JPG、PNG"
        />

        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200/60">
          <h4 className="text-xs font-semibold text-foreground-700 mb-2">认证须知</h4>
          <ul className="space-y-1">
            <li className="text-xs text-foreground-500 flex items-start gap-1.5">
              <i className="ri-information-line text-secondary-500 mt-0.5"></i>
              请确保上传的身份证照片清晰、完整、无遮挡
            </li>
            <li className="text-xs text-foreground-500 flex items-start gap-1.5">
              <i className="ri-information-line text-secondary-500 mt-0.5"></i>
              认证信息提交后无法修改，请仔细核对
            </li>
            <li className="text-xs text-foreground-500 flex items-start gap-1.5">
              <i className="ri-information-line text-secondary-500 mt-0.5"></i>
              审核通常需要 1-3 个工作日
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 py-3 rounded-lg bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {submitting ? '提交中...' : '提交个人认证'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-lg border border-gray-200 text-foreground-600 text-sm hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
        </div>
      </form>
    </div>
  );
};

